import { useCallback, useEffect, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react/hooks';
import { GET_ORG_PAGE_QUERY } from '@/libs/onboarding/queries';
import { Organization } from '@/libs/onboarding/types';

type OrgPageData = {
  me: {
    organizationPage: {
      organizations: Organization[];
      cursor?: string;
    };
  };
};

/**
 * Cursor-paginates `me.organizationPage` and accumulates the user's orgs.
 * Mirrors the pos-app / cds-app / kds-app pattern; `me.organizations` caps
 * support users at one org so it isn't usable here.
 */
export const useUserOrgs = () => {
  const client = useApolloClient();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApolloError | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);

  const fetchOrgs = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setOrganizations([]);
    setError(undefined);
    setLoading(true);

    const accumulated: Organization[] = [];
    try {
      let cursor: string | undefined;
      do {
        const res = await client.query<OrgPageData>({
          query: GET_ORG_PAGE_QUERY,
          variables: { cursor, limit: 100 },
          fetchPolicy: 'no-cache',
          // Disable dedup so concurrent fetches don't share an observable
          // and an abort on one can't error out the other.
          context: {
            queryDeduplication: false,
            fetchOptions: { signal: controller.signal },
          },
        });
        const page = res.data?.me?.organizationPage;
        for (const org of page?.organizations ?? []) {
          if (org?.id && org?.name) {
            accumulated.push({ id: org.id, name: org.name });
          }
        }
        cursor = page?.cursor;
      } while (cursor);

      if (controller.signal.aborted) return;
      setOrganizations(accumulated);
    } catch (e) {
      if (controller.signal.aborted) return;
      setError(
        e instanceof ApolloError
          ? e
          : new ApolloError({
              errorMessage: (e as Error)?.message ?? 'Unknown error',
            }),
      );
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return {
    organizations,
    loading,
    error,
    fetchOrgs,
  };
};
